import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '../utils/toast';
import { Plus, Edit, XCircle, CheckCircle, Loader2, Eye } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExlvaAdminManagementPage = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [includeDeactivated, setIncludeDeactivated] = useState(false);
  const [viewCredentialsDialog, setViewCredentialsDialog] = useState(false);
  const [selectedAdminForCredentials, setSelectedAdminForCredentials] = useState(null);
  const [newTempPassword, setNewTempPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    institution_name: 'Exlva Healthcare Solutions',
    password: '',
    role: 'ExlvaAdmin'
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.role !== 'Super Admin') {
      toast.error('Only Super Admin can manage Exlva admins');
      navigate('/super-admin');
      return;
    }
    fetchAdmins();
  }, [navigate, includeDeactivated]);

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/exlva-admins`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_deactivated: includeDeactivated }
      });
      setAdmins(response.data);
    } catch (error) {
      toast.error('Failed to fetch Exlva admins');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (admin = null) => {
    if (admin) {
      setEditMode(true);
      setSelectedAdmin(admin);
      setFormData({
        full_name: admin.full_name,
        email: admin.email,
        phone_number: admin.phone_number,
        institution_name: admin.institution_name,
        password: '',
        role: admin.role
      });
    } else {
      setEditMode(false);
      setSelectedAdmin(null);
      setFormData({
        full_name: '',
        email: '',
        phone_number: '',
        institution_name: 'Exlva Healthcare Solutions',
        password: '',
        role: 'ExlvaAdmin'
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.phone_number || !formData.role) {
      toast.error('All fields are required');
      return;
    }

    if (!editMode && !formData.password) {
      toast.error('Password is required for new admins');
      return;
    }

    setFormLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editMode) {
        await axios.put(
          `${API}/super-admin/exlva-admins/${selectedAdmin.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Admin updated successfully');
      } else {
        await axios.post(
          `${API}/super-admin/exlva-admins`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Admin created successfully');
      }
      
      setDialogOpen(false);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save admin');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeactivate = async (adminId) => {
    if (!window.confirm('Are you sure you want to deactivate this admin?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/super-admin/exlva-admins/${adminId}/deactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Admin deactivated successfully');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate admin');
    }
  };

  const handleReactivate = async (adminId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/super-admin/exlva-admins/${adminId}/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Admin reactivated successfully');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate admin');
    }
  };

  const handleViewCredentials = (admin) => {
    setSelectedAdminForCredentials(admin);
    setNewTempPassword('');
    setViewCredentialsDialog(true);
  };

  const handleResetPassword = async () => {
    if (!selectedAdminForCredentials) return;
    
    // Generate a random temporary password
    const tempPassword = Math.random().toString(36).slice(-10) + Math.random().toString(36).slice(-10).toUpperCase();
    
    setResettingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/super-admin/exlva-admins/${selectedAdminForCredentials.id}`,
        { password: tempPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewTempPassword(tempPassword);
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">EXLVA ADMIN MANAGEMENT</h1>
            <p className="text-sm text-gray-600 mt-1">Manage ExlvaAdmin and ExlvaAdminLite users</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setIncludeDeactivated(!includeDeactivated)}
              variant="outline"
            >
              {includeDeactivated ? 'Hide' : 'Show'} Deactivated
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => handleOpenDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Admin
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editMode ? 'Edit Admin' : 'Add New Admin'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="admin@exlva.com"
                      disabled={editMode}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone_number">Phone Number *</Label>
                    <Input
                      id="phone_number"
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                      placeholder="+1234567890"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="role">Role *</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value) => setFormData({ ...formData, role: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ExlvaAdmin">ExlvaAdmin</SelectItem>
                        <SelectItem value="ExlvaAdminLite">ExlvaAdminLite</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.role === 'ExlvaAdminLite' 
                        ? 'Limited access: Training Chapters, Question Bank, Email Templates only'
                        : 'Full admin access except CRM, Demo Requests, and CMS Settings'}
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="password">
                      Password {editMode ? '(leave blank to keep unchanged)' : '*'}
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Enter password"
                      required={!editMode}
                    />
                  </div>
                  
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={formLoading}>
                      {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {editMode ? 'Update' : 'Create'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Admin List */}
        <div className="bg-white rounded-lg shadow">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#0056B3]" />
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No admins found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell className="font-medium">{admin.full_name}</TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>{admin.phone_number}</TableCell>
                    <TableCell>
                      <Badge variant={admin.role === 'ExlvaAdminLite' ? 'secondary' : 'default'}>
                        {admin.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {admin.is_active ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="destructive">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewCredentials(admin)}
                          data-tooltip="View Credentials"
                        >
                          <Eye className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(admin)}
                          data-tooltip="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {admin.is_active ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeactivate(admin.id)}
                            data-tooltip="Deactivate"
                          >
                            <XCircle className="w-4 h-4 text-red-600" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReactivate(admin.id)}
                            data-tooltip="Reactivate"
                          >
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Role Information */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-2">Role Permissions</h3>
          <div className="space-y-2 text-sm text-gray-700">
            <div>
              <strong>ExlvaAdmin:</strong> Full admin access including Dashboard, Institutions, Default Org Workflow, 
              Training Chapters, Question Bank, Feedback, Evaluation Form, Subscriptions, and Email Templates.
            </div>
            <div>
              <strong>ExlvaAdminLite:</strong> Restricted access to Training Chapters, Question Bank, and Email Templates only.
            </div>
          </div>
        </div>
      </div>

      {/* View Credentials Dialog */}
      <Dialog open={viewCredentialsDialog} onOpenChange={setViewCredentialsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Admin Credentials</DialogTitle>
          </DialogHeader>
          {selectedAdminForCredentials && (
            <div className="space-y-4">
              {/* Admin Info */}
              <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                <div>
                  <label className="text-xs text-gray-500">Full Name</label>
                  <p className="text-sm font-medium text-gray-900">{selectedAdminForCredentials.full_name}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Role</label>
                  <p className="text-sm font-medium text-gray-900">{selectedAdminForCredentials.role}</p>
                </div>
              </div>

              {/* Username */}
              <div>
                <Label>Username (Email)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={selectedAdminForCredentials.email}
                    readOnly
                    className="bg-gray-50"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(selectedAdminForCredentials.email, 'Username')}
                  >
                    Copy
                  </Button>
                </div>
              </div>

              {/* Password Subchapter */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label>Password</Label>
                  {!newTempPassword && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleResetPassword}
                      disabled={resettingPassword}
                    >
                      {resettingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Reset Password
                    </Button>
                  )}
                </div>
                {newTempPassword ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={newTempPassword}
                        readOnly
                        className="bg-green-50 border-green-300 font-mono"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(newTempPassword, 'Password')}
                      >
                        Copy
                      </Button>
                    </div>
                    <p className="text-xs text-green-600">
                      ✓ New temporary password generated. Please share this with the admin securely.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 bg-yellow-50 border border-yellow-200 rounded p-3">
                    ⓘ Passwords are securely encrypted and cannot be viewed. Click "Reset Password" to generate 
                    a new temporary password that will be displayed here.
                  </p>
                )}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setViewCredentialsDialog(false)}
                >
                  Close
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ExlvaAdminManagementPage;
